import { AnalyticService, AnalyticCategories } from '@application/analytic/analytic.service';
import { I18nAndL10n } from '@application/i18n/i18n_and_l10n';
import { withTranslation } from '@application/i18n/with_translation';
import { DialogAlertSuccessError, DialogAlert, Button } from '@cellula/react';
import { Article } from '@component/article.component';
import { useResolve } from '@visiba/instantiation';
import { MyAccountController } from './my_account.controller';
import { UniversalConfig } from '@application/universal_config.service';

interface MyAccountDeleteButtonProps {
	canDelete: boolean;
}

export function MyAccountDeleteButton({ canDelete }: MyAccountDeleteButtonProps) {
	const universalConfig = useResolve(UniversalConfig);
	const controller = useResolve(MyAccountController);
	const analyticService = useResolve(AnalyticService);

	async function handleDeleteAccount() {
		analyticService.trackEvent(AnalyticCategories.MyAccount, 'Delete account');

		try {
			await controller.delete();
		} catch {
			analyticService.trackEvent(AnalyticCategories.MyAccount, 'Delete account error');
			throw new DialogAlertSuccessError(I18nAndL10n.translate('PW.Feature.MyAccount.DeleteError'));
		}
	}

	function onDialogOpenChange(isOpen: boolean) {
		if (isOpen) {
			analyticService.trackEvent(
				AnalyticCategories.MyAccount,
				canDelete ? 'Open delete account dialog' : "Open can't delete account dialog",
			);
		}
	}

	function onDialogAbort() {
		analyticService.trackEvent(
			AnalyticCategories.MyAccount,
			canDelete ? 'Close delete account dialog' : "Close can't delete account dialog",
		);
	}

	let successProps = {};

	if (canDelete) {
		successProps = {
			successText: withTranslation('PW.Feature.MyAccount.DeleteDialog.SuccessText'),
			onSuccess: handleDeleteAccount,
		};
	}

	return (
		<DialogAlert
			header={withTranslation(canDelete ? 'PW.Feature.MyAccount.DeleteDialog.Header' : 'PW.Feature.MyAccount.CantDeleteDialog.Header')}
			description={''}
			abortText={withTranslation(
				canDelete ? 'PW.Feature.MyAccount.DeleteDialog.AbortText' : 'PW.Feature.MyAccount.CantDeleteDialog.AbortText',
			)}
			{...successProps}
			trigger={
				<Button variant='ghost' icon='bin'>
					{withTranslation('PW.Feature.MyAccount.DeleteButton')}
				</Button>
			}
			onOpenChange={onDialogOpenChange}
			onAbort={onDialogAbort}
		>
			<Article>
				{canDelete
					? withTranslation('PW.Feature.MyAccount.DeleteDialog.Description', {
							daysToDepersonalizeClient: `${universalConfig.daysToDepersonalizeClientAfterRequest ?? '-'}`,
						})
					: withTranslation('PW.Feature.MyAccount.CantDeleteDialog.Description')}
			</Article>
		</DialogAlert>
	);
}
